// ─── Uploading a video from the visual editor ─────────────────────────────────
// The file goes up as the request body itself, to POST /api/upload/video, and
// the server streams it to disk (services/videoUpload.js). It used to go as
// base64 inside JSON, which capped a video at 4 MB — a few seconds of phone
// footage — and turned away an iPhone's .mov altogether.
//
// XMLHttpRequest rather than fetch, because fetch reports no upload progress,
// and a 60 MB clip on a phone connection takes long enough that a spinner
// with no number looks like a hang.

import { supabase } from '@/lib/supabase';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/** The server's default VIDEO_UPLOAD_MAX_MB. */
export const MAX_VIDEO_MB = 100;

const TYPES = { mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };
const ACCEPTED = new Set(Object.values(TYPES));

/** The file chooser's filter: the types, and the extensions for when the browser names no type. */
export const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov';

/**
 * The video's type, from the browser or else from its name; '' when it is not
 * one we take. Windows names no type for a .mov without QuickTime installed.
 */
export function videoTypeOf(file) {
  const type = String(file?.type || '').toLowerCase();
  if (ACCEPTED.has(type)) return type;
  if (type && type !== 'application/octet-stream') return '';
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  return TYPES[ext] || '';
}

/**
 * Upload a video and return the URL it is served at.
 *
 * @param {File} file
 * @param {{onProgress?: (percent: number) => void, maxMb?: number}} [opts]
 * @returns {Promise<string>} the public URL
 * @throws {Error} with a message meant for the person uploading
 */
export async function uploadVideo(file, { onProgress, maxMb = MAX_VIDEO_MB } = {}) {
  const type = videoTypeOf(file);
  if (!type) throw new Error('Choose an MP4, WebM or MOV video.');
  if (file.size > maxMb * 1024 * 1024) {
    throw new Error(`That video is ${(file.size / 1048576).toFixed(0)} MB. Videos can be up to ${maxMb} MB: trim it, or paste a YouTube or Vimeo link instead.`);
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/upload/video`);
    xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
    xhr.setRequestHeader('Content-Type', type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.min(99, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* a proxy's own error page */ }
      if (xhr.status >= 200 && xhr.status < 300 && data.publicUrl) {
        onProgress?.(100);
        resolve(data.publicUrl);
      } else if (xhr.status === 413) {
        // Ours says how big; a proxy in front of us says nothing useful.
        reject(new Error(data.error || `That video is too big to upload. Videos can be up to ${maxMb} MB: trim it, or paste a YouTube or Vimeo link instead.`));
      } else {
        reject(new Error(data.error || 'The video could not be uploaded. Try again.'));
      }
    };
    xhr.onerror = () => reject(new Error('The upload stopped. Check your connection and try again.'));
    xhr.send(file);
  });
}
